import React, { useState, useEffect } from 'react'
import { styled } from '@mui/material'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'

// Components
import StyledPageContainer from '../../components/StyledPageContainer/StyledPageContainer'
import StyledPageHeader from '../../components/StyledPageHeader/StyledPageHeader'
import StyledTable from '../../components/ReactTable/StyledTable'
import StyledTableContainer from '../../components/StyledTableContainer/StyledTableContainer'
import StyledEmptyHeader from '../../components/StyledEmptyHeader/StyledEmptyHeader'
import StyledSelectField from '../../components/StyledSelectField/StyledSelectField'
import { useDateSelectorContext } from '../../context/DateSelectorContext'
import PressingMachineWrapper from './PressingMachineWrapper'

// Services
import liveDataService from '../../service/liveData.service'

const BoxContainer = styled('div')(() => ({
  width: '100%',
  height: '70vh',
  overflow: 'auto',
  border: '1px solid #ccc',
  borderRadius: '8px',
  padding: '16px',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
}))

const PressingMachineData = () => {
  const [selectedMachine, setSelectedMachine] = useState({
    label: '',
    value: ''
  })
  const [pressingMachineRows, setPressingMachineRows] = useState([])
  const { selectedDate } = useDateSelectorContext()
  const [availableShifts, setAvailableShifts] = useState([])
  const [selectedShift, setSelectedShift] = useState({
    label: 'All shifts',
    value: 0
  })
  const [rawGraphData, setRawGraphData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [availableMachines, setAvailableMachines] = useState([])

  const fetchPressingMachineData = async () => {
    const params = {}

    if (selectedDate) {
      params.date = selectedDate
    }

    try {
      setIsLoading(true)
      const response = await liveDataService.getPressingMachineData(params)

      const { dayData = [], graphData = [] } = response.data

      const availableShifts = []
      const uniqueMachines = [...new Set(dayData.map((item) => item.machine))]
      const partsData = dayData.map((item, i) => {
        const shiftValue = `S${item.shift}`
        if (!availableShifts.includes(shiftValue)) {
          availableShifts.push(shiftValue)
        }

        return {
          index: i + 1,
          date: item.date,
          time: item.time,
          shift: shiftValue,
          machine: item.machine,
          partName: item.partName,
          partType: item.partType,
          workOrderNum: item.workOrderNum,
          noOfTrays: item.noOfTrays,
          trayNumber: item.trayNumber,
          bushLoad: item.bushLoad,
          minLoad: item.minLoad,
          maxLoad: item.maxLoad,
          approachTime: item.approachTime,
          powerStrokeTime: item.powerStrokeTime
        }
      })

      const machineOptions = uniqueMachines.map((machine) => ({
        label: machine,
        value: machine
      }))

      if (machineOptions.length > 1) {
        setSelectedMachine(machineOptions[1])
      } else if (machineOptions.length === 1) {
        setSelectedMachine(machineOptions[0])
      }

      setAvailableMachines(machineOptions)
      setAvailableShifts([
        {
          label: 'All shifts',
          value: 0
        },
        ...availableShifts.map((shift) => {
          return { label: shift, value: shift }
        })
      ])

      setPressingMachineRows(partsData)
      setRawGraphData(graphData)
    } catch (err) {
      console.error('Error fetching data:', err)
      setPressingMachineRows([])
      setRawGraphData([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchPressingMachineData()
  }, [selectedDate])

  const searchObj = {
    ...(selectedMachine &&
      (selectedMachine.value !== 'all' || selectedMachine.value === 0) && {
        machine: selectedMachine.value
      }),
    ...(selectedShift &&
      selectedShift.value &&
      selectedShift.value !== 0 && {
        shift: selectedShift.value
      })
  }

  const keysToChecked = Object.keys(searchObj)

  // Filter table data
  let filteredRows = pressingMachineRows
  if (keysToChecked.length > 0) {
    filteredRows = pressingMachineRows.filter((pressingMachineRow) => {
      return keysToChecked.every(
        (key) => searchObj[key] === pressingMachineRow[key]
      )
    })
  }

  // Filter graph data
  const filteredGraphData = rawGraphData.filter((item) => {
    const matchesMachine =
      !selectedMachine.value ||
      selectedMachine.value === 'all' ||
      item.machine === selectedMachine.value

    const matchesShift =
      !selectedShift.value ||
      selectedShift.value === 0 ||
      `S${item.shift}` === selectedShift.value

    return matchesMachine && matchesShift
  })

  const formatToTwoDecimalPlaces = (value) => {
    if (typeof value === 'number') {
      return value.toFixed(2)
    }
    return value
  }

  const pressingMachineColumns = [
    { Header: 'S.No', accessor: 'index' },
    { Header: 'Machine', accessor: 'machine' },
    { Header: 'Time', accessor: 'time' },
    { Header: 'Shift', accessor: 'shift' },
    { Header: 'Part name', accessor: 'partName' },
    { Header: 'Part type', accessor: 'partType' },
    { Header: 'Work order num', accessor: 'workOrderNum' },
    { Header: 'Num of trays', accessor: 'noOfTrays' },
    { Header: 'Tray number', accessor: 'trayNumber' },
    {
      Header: 'Bush load',
      accessor: 'bushLoad',
      Cell: ({ value }) => (value === 0 ? 'null' : value)
    },

    { Header: 'Min load', accessor: 'minLoad' },
    { Header: 'Max load', accessor: 'maxLoad' },
    {
      Header: 'Approach time',
      accessor: 'approachTime',
      Cell: ({ value }) => formatToTwoDecimalPlaces(value)
    },
    {
      Header: 'Power stroke time',
      accessor: 'powerStrokeTime',
      Cell: ({ value }) => formatToTwoDecimalPlaces(value)
    }
  ]

  return (
    <>
      <Stack
        sx={{
          position: 'sticky',
          top: 0,
          width: '100%',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '24px',
          padding: '16px',
          backgroundColor: '#fff',
          borderBottom: '1px solid #eeeeee',
          zIndex: 20
        }}
      >
        <StyledSelectField
          label=""
          placeHolderText="Select shift"
          selectedValue={selectedShift}
          selectOptions={availableShifts}
          onChangeHandler={(value) => setSelectedShift(value)}
          isMulti={false}
          isSearchable={true}
          isDisabled={false}
          noOptionsMessage="No shift found"
        />
        <StyledSelectField
          label=""
          placeHolderText=""
          selectedValue={selectedMachine}
          selectOptions={availableMachines}
          onChangeHandler={(value) => setSelectedMachine(value)}
          isMulti={false}
          isSearchable={true}
          isDisabled={false}
          noOptionsMessage="No machine found"
        />
      </Stack>

      <Stack sx={{ gap: '16px', padding: '16px' }}>
        {!isLoading && (
          <PressingMachineWrapper pressingMachine={filteredGraphData} />
        )}
        <StyledTableContainer>
          {isLoading && <StyledEmptyHeader>Loading...</StyledEmptyHeader>}
          {!isLoading && filteredRows.length === 0 && (
            <StyledEmptyHeader>
              No pressing machine data found
            </StyledEmptyHeader>
          )}

          {!isLoading && filteredRows.length > 0 && (
            <BoxContainer>
              <StyledTable
                data={filteredRows}
                columns={pressingMachineColumns}
                pagination={false}
                hiddenColumns={['index']}
              />
            </BoxContainer>
          )}
        </StyledTableContainer>
      </Stack>
    </>
  )
}

export default PressingMachineData
